import JsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { pdfHeader } from "@/utilities/pdfUtils";
import {
  formatFeeName
} from "@/utilities/settings";
import {
  formatCurrency,
  getBuildingCurrency,
} from "@/utilities/countrySpecific";

const TABLE_COL_COUNT = 9;

const isIsoDateKey = (key) => moment(key, moment.ISO_8601, true).isValid();

const isMonthlyFeeKey = (key, daily) => {
  if (!isIsoDateKey(key)) return false;
  return moment(key).format("DD") === "01" && daily == false;
};

const formatMonthLabel = (key) => {
  const month = moment(key).format("MMMM");
  return month.charAt(0).toUpperCase() + month.slice(1);
};

/**
 * Compact Frais cell: monthly fees stay as month names;
 * several par-jour / par-séance dates become a count + range instead of one line per date.
 */
const formatFeesForPdf = (fees, daily, t) => {
  if (!fees || typeof fees !== "object") return "--";
  const keys = Object.keys(fees);
  if (!keys.length) return "--";

  const otherLabels = [];
  const monthlyLabels = [];
  const dailyDates = [];

  keys.forEach((key) => {
    if (isMonthlyFeeKey(key, daily)) {
      monthlyLabels.push(formatMonthLabel(key));
    } else if (isIsoDateKey(key)) {
      dailyDates.push(moment(key));
    } else {
      otherLabels.push(formatFeeName(key, daily, t));
    }
  });

  dailyDates.sort((a, b) => a.valueOf() - b.valueOf());

  const parts = [];
  if (otherLabels.length) parts.push(otherLabels.join(", "));
  if (monthlyLabels.length) parts.push(monthlyLabels.join(", "));
  if (dailyDates.length === 1) {
    parts.push(dailyDates[0].format("DD/MM/YYYY"));
  } else if (dailyDates.length > 1) {
    parts.push(
      t("recette.pdfDailyFeesCount", { count: dailyDates.length })
    );
    parts.push(
      `${dailyDates[0].format("DD/MM/YYYY")} → ${dailyDates[
        dailyDates.length - 1
      ].format("DD/MM/YYYY")}`
    );
  }

  return parts.filter(Boolean).join("\n") || "--";
};

const buildColumnStyles = (isArabic) => {
  const ltr = {
    0: { cellWidth: 22, overflow: "ellipsize" },
    1: { cellWidth: 28, overflow: "ellipsize" },
    2: { cellWidth: 34, overflow: "ellipsize" },
    3: { cellWidth: 34, overflow: "ellipsize" },
    4: { cellWidth: 28, overflow: "ellipsize" },
    5: { cellWidth: 58, overflow: "linebreak" },
    6: { cellWidth: 24, overflow: "ellipsize" },
    7: { cellWidth: 24, overflow: "ellipsize" },
    8: { cellWidth: 20, overflow: "ellipsize" },
  };
  if (!isArabic) return ltr;
  const rtl = {};
  Object.keys(ltr).forEach((key) => {
    rtl[TABLE_COL_COUNT - 1 - Number(key)] = ltr[key];
  });
  return rtl;
};

/**
 * Generate summary data for payment methods
 * @param {Array} data - The data to analyze
 * @param {string} currency - Currency code to use for formatting
 * @param {Function} t - Translation function (i18n.t)
 * @returns {Object} Summary data with body, totalQuantity, and totalAmount
 */
const generateSummaryData = (data, currency, t) => {
  const paymentMethodStats = {};
  let totalAmount = 0;

  // Process each record
  data.forEach((record) => {
    const amount = record.montantTotal || 0;
    totalAmount += amount;

    if (Array.isArray(record.paymentMethod)) {
      // Handle multiple payment methods
      record.paymentMethod.forEach((method) => {
        if (!paymentMethodStats[method]) {
          paymentMethodStats[method] = {
            quantity: 0,
            amount: 0,
          };
        }
        paymentMethodStats[method].quantity += 1;
        paymentMethodStats[method].amount += amount;
      });
    } else if (record.paymentMethod) {
      // Handle single payment method
      const method = record.paymentMethod;
      if (!paymentMethodStats[method]) {
        paymentMethodStats[method] = {
          quantity: 0,
          amount: 0,
        };
      }
      paymentMethodStats[method].quantity += 1;
      paymentMethodStats[method].amount += amount;
    }
  });

  // Convert to array format for the table
  const body = Object.entries(paymentMethodStats).map(([method, stats]) => {
    const percentage =
      totalAmount > 0
        ? ((stats.amount / totalAmount) * 100).toFixed(2)
        : "0.00";
    const translatedMethod = t ? t(`recette.paymentMethod.${method}`) : method;

    return [
      translatedMethod,
      stats.quantity.toString(),
      formatCurrency(stats.amount, currency),
      `${percentage}%`,
    ];
  });

  // Sort by amount (descending)
  body.sort((a, b) => {
    const amountA = parseFloat(a[2].replace(/[^\d.,]/g, "").replace(",", "."));
    const amountB = parseFloat(b[2].replace(/[^\d.,]/g, "").replace(",", "."));
    return amountB - amountA;
  });

  const totalQuantity = Object.values(paymentMethodStats).reduce(
    (sum, stats) => sum + stats.quantity,
    0
  );

  return {
    body,
    totalQuantity,
    totalAmount,
  };
};

/**
 * Generate PDF for Etat des Recettes
 * @param {Array} data - The data to include in the PDF
 * @param {Object} settings - Application settings including images
 * @param {Array} dateRangeData - Date range filter data
 * @param {Object} schoolarYearNames - Map of school year id to its name
 * @returns {Promise<void>}
 */
export const generateEtatDesRecettesPdf = async (
  data,
  settings,
  dateRangeData,
  t,
  locale = "fr-FR",
  schoolarYearNames = {},
  caisseNames = [],
  isAllCaisses = false
) => {
  try {
    const currency = getBuildingCurrency();
    const isArabic = locale === "ar-AR";
    const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);

    // Use the existing pdfHeader function
    const filterText =
      dateRangeData && dateRangeData[0] && dateRangeData[1]
        ? `${t("recette.dateRangeFrom")} ${moment(dateRangeData[0]).format("DD/MM/YYYY")} ${t("recette.dateRangeTo")} ${moment(
            dateRangeData[1]
          ).format("DD/MM/YYYY")}`
        : t("recette.toutesDates");

    const titre = t("encaissement.stateOfReceipts");
    const doc = pdfHeader(settings, {
      title: titre,
      subTitle: filterText,
      locale: locale,
      portrait: false,
    });
    const pageWidth = doc.internal.pageSize.getWidth();

    const caisseHeaderValue = isAllCaisses
      ? t("recette.tous")
      : Array.isArray(caisseNames) && caisseNames.length
        ? caisseNames.join(", ")
        : "--";
    const caisseHeaderText = `${t("encaissement.cashRegister")}: ${caisseHeaderValue}`;
    const caisseHeaderLines = doc.splitTextToSize(caisseHeaderText, pageWidth - 30);
    const caisseHeaderStartY = 34;
    doc.setFontSize(10);
    caisseHeaderLines.forEach((line, index) => {
      doc.text(line, pageWidth / 2, caisseHeaderStartY + index * 4, {
        align: "center",
      });
    });

    // Legend
    doc.setFontSize(8);
    doc.setFillColor("#FFCC66");
    doc.setDrawColor("#FFCC66");
    const legendY = caisseHeaderStartY + (caisseHeaderLines.length - 1) * 4 + 6;
    const legendX = isArabic ? pageWidth - 19 : 15; // Right for Arabic, left for others
    const legendTextX = isArabic ? pageWidth - 22 : 22; // Right for Arabic, left for others
    doc.rect(legendX, legendY, 4, 4, "FD");
    doc.text(legendTextX, legendY + 3, t("encaissement.creditNote"), {
      align: isArabic ? "right" : "left",
    });

    // Prepare table data
    const array = [];
    let sommeTTC = 0;

    for (let i = 0; i < data.length; i++) {
      const record = data[i];

      // Get client name from addressedTo
      const nomClient = record.addressedTo?.name || "--";

      // The list spans several years, so each row states the one it belongs to
      const schoolarYearName = schoolarYearNames[record.schoolarYear] || "--";

      // Format payment method
      const paymentMethodArray = Array.isArray(record.paymentMethod)
        ? record.paymentMethod
        : record.paymentMethod
          ? [record.paymentMethod]
          : [];
      const modePaiement = paymentMethodArray
        .map((method) => {
          // Translate payment method using translation keys
          return t(`recette.paymentMethod.${method}`) || method;
        })
        .join(", ");

      const montantTotalText = (record.montantTotal || 0).toFixed(2);
      sommeTTC += record.montantTotal || 0;

      // Get details array
      const details = record.details || [];
      const detailsCount = details.length;

      // If no details, create a single row with empty data
      if (detailsCount === 0) {
        let row = [
          {
            content: record.serial || "--",
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
          {
            content: schoolarYearName,
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
          {
            content: nomClient,
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
          {
            content: "--",
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
          {
            content: "--",
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
          {
            content: "--",
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
          {
            content: modePaiement,
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
          {
            content: record.doneBy?.userName || "--",
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
          {
            content: montantTotalText,
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          },
        ];

        // Reverse for Arabic if needed
        if (isArabic) {
          row.reverse();
        }

        array.push(row);
      } else {
        // Create a row for each detail
        for (let j = 0; j < details.length; j++) {
          const detail = details[j];
          const isFirstDetail = j === 0;

          let row = [];

          // Serial column - only show on first detail, with rowspan
          if (isFirstDetail) {
            row.push({
              content: record.serial || "--",
              rowSpan: detailsCount,
              styles:
                record.cancellation && record.cancellation.type === "Avoir"
                  ? { fillColor: [255, 234, 153] }
                  : {},
            });
          }

          // School year column - only show on first detail, with rowspan
          if (isFirstDetail) {
            row.push({
              content: schoolarYearName,
              rowSpan: detailsCount,
              styles:
                record.cancellation && record.cancellation.type === "Avoir"
                  ? { fillColor: [255, 234, 153] }
                  : {},
            });
          }

          // Parent column - only show on first detail, with rowspan
          if (isFirstDetail) {
            row.push({
              content: nomClient,
              rowSpan: detailsCount,
              styles:
                record.cancellation && record.cancellation.type === "Avoir"
                  ? { fillColor: [255, 234, 153] }
                  : {},
            });
          }

          // Student column - show for each detail
          row.push({
            content: detail.studentName || "--",
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          });

          // Type reglement column - show for each detail
          row.push({
            content: detail.typeReglement || "--",
            styles:
              record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {},
          });

          row.push({
            content: formatFeesForPdf(detail.fees, detail.daily, t),
            styles: {
              overflow: "linebreak",
              ...(record.cancellation && record.cancellation.type === "Avoir"
                ? { fillColor: [255, 234, 153] }
                : {}),
            },
          });

          // Payment method - only show on first detail, with rowspan
          if (isFirstDetail) {
            row.push({
              content: modePaiement,
              rowSpan: detailsCount,
              styles:
                record.cancellation && record.cancellation.type === "Avoir"
                  ? { fillColor: [255, 234, 153] }
                  : {},
            });
          }

          // DoneBy column - only show on first detail, with rowspan
          if (isFirstDetail) {
            row.push({
              content: record.doneBy?.userName || "--",
              rowSpan: detailsCount,
              styles:
                record.cancellation && record.cancellation.type === "Avoir"
                  ? { fillColor: [255, 234, 153] }
                  : {},
            });
          }

          // Montant total - only show on first detail, with rowspan
          if (isFirstDetail) {
            row.push({
              content: montantTotalText,
              rowSpan: detailsCount,
              styles:
                record.cancellation && record.cancellation.type === "Avoir"
                  ? { fillColor: [255, 234, 153] }
                  : {},
            });
          }

          // Reverse for Arabic if needed
          if (isArabic) {
            row.reverse();
          }

          array.push(row);
        }
      }
    }

    // Generate table
    doc.autoTable({
      startY: legendY + 8,
      margin: { left: 10, right: 10 },
      tableWidth: "wrap",
      styles: {
        font: "Amiri",
        fontSize: 7,
        cellPadding: 1.2,
        valign: "middle",
        halign: "center",
        overflow: "linebreak",
      },
      headStyles: {
        fontSize: 7,
        fontStyle: "bold",
        overflow: "linebreak",
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
      },
      columnStyles: buildColumnStyles(isArabic),
      alternateRowStyles: {
        fillColor: false,
      },
      bodyStyles: {
        fillColor: false,
        lineWidth: 0.05,
        lineColor: [200, 200, 200],
      },
      head: [
        reverseIfArabic([
          t("recette.nSerie"),
          t("recette.anneeScolaire"),
          t("recette.parent"),
          t("encaissement.eleve"),
          t("recette.typeReglement"),
          t("recette.frais"),
          t("recette.modePaiement"),
          t("recette.effectuePar"),
          t("recette.montantTTC"),
        ]),
      ],
      body: array,
      foot: [
        reverseIfArabic([
          {
            colSpan: 8,
            content: t("liste.total"),
            styles: { halign: "center" },
          },
          formatCurrency(sommeTTC, currency),
        ]),
      ],
    });

    // Generate summary table
    const summaryData = generateSummaryData(data, currency, t);

    // Add title for summary table
    doc.setFontSize(12);
    doc.setFont("Amiri", "Bold");
    const titleText = t("encaissement.summaryByPaymentMethod");
    const titleX = isArabic ? pageWidth - 15 : 15;
    doc.text(titleText, titleX, doc.autoTableEndPosY() + 15, {
      align: isArabic ? "right" : "left",
    });
    doc.setFont("Amiri", "normal");
    doc.autoTable({
      startY: doc.autoTableEndPosY() + 20,
      styles: {
        font: "Amiri",
        valign: "middle",
        halign: "center",
        fontSize: 10,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      bodyStyles: {
        lineWidth: 0.1,
        lineColor: [100, 100, 100],
      },
      head: [
        reverseIfArabic([
          t("recette.modePaiement"),
          t("encaissement.quantite"),
          t("recette.montantTotal"),
          t("etablissement.pourcentage"),
        ]),
      ],
      body: summaryData.body.map((row) =>
        isArabic ? row.slice().reverse() : row
      ),
      foot: [
        reverseIfArabic([
          {
            content: t("encaissement.totalGeneral"),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [52, 73, 94],
              textColor: [255, 255, 255],
            },
          },
          {
            content: summaryData.totalQuantity.toString(),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [52, 73, 94],
              textColor: [255, 255, 255],
            },
          },
          {
            content: formatCurrency(summaryData.totalAmount, currency),
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [52, 73, 94],
              textColor: [255, 255, 255],
            },
          },
          {
            content: "100%",
            styles: {
              halign: "center",
              fontStyle: "bold",
              fillColor: [52, 73, 94],
              textColor: [255, 255, 255],
            },
          },
        ]),
      ],
    });

    // Add page numbering
    addPageNumbers(doc, t);

    // Save the PDF
    doc.save(`${t("encaissement.stateOfReceipts")}.pdf`);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error(t("recette.pdfGenerationError"));
  }
};

/**
 * Add page numbers to the PDF
 * @param {JsPDF} doc - The PDF document
 */
const addPageNumbers = (doc, t) => {
  const pages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  doc.setFontSize(8);

  for (let j = 1; j < pages + 1; j++) {
    const horizontalPos = pageWidth / 2;
    const verticalPos = pageHeight - 10;

    doc.setPage(j);
    doc.text(
      `${t("pdf.pageNumber", { current: j, total: pages })}`,
      horizontalPos,
      verticalPos,
      {
        align: "center",
      }
    );
  }
};

